<template>
  <el-col :span="24" id="prs" v-loading.body="(changingPage || changingTab) && !navigating" element-loading-text="Fetching page...">
    <ListViewHeader
      title="PAYROLL ENTRIES"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :hasCreatePermission="hasCreatePermission"
      :createRouteName="'NewPr'">
    </ListViewHeader>
    <el-row type="flex" justify="start" align="middle" style="padding: 0 12px; margin: 12px 0;">
      <el-col :span="8">
        <el-row type="flex" justify="start" align="middle">
          <el-button-group>
            <el-button
              :type="activeTab === 'open' ? 'primary' : null"
              :class="activeTab === 'open' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('open')">
              View Open Payrolls
            </el-button>
            <el-button
              :type="activeTab === 'posted' ? 'primary' : null"
              :class="activeTab === 'posted' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('posted')">
              View Posted Payrolls
            </el-button>
            <el-button
              :type="activeTab === 'all' ? 'primary' : null"
              :class="activeTab === 'all' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('all')">
              View All Payrolls
            </el-button>
          </el-button-group>
        </el-row>
      </el-col>
      <el-col :span="16">
        <el-row type="flex" justify="end" align="middle" style="padding: 0 12px;">
          <Filters
            ref="filters"
            :filters="filters"
            :filtersVisible="showFilters"
            :filterSize="7"
            :autoApply="false"
            clearAction="clearJeState"
            clearMetadataAction="resetJeMeta"
            searchAction="fetchJesWithQuery"
            transactionType="Pr"
            :lineFieldSearch="otherNameFieldEnabled ? { field: 'other_name', transactionType: 'pr' } : null"
            @filterCleared="() => $refs.filters.applyFilters()"
            :defaultParams ="{source_code: prSourcecodeId}"
            @doneFiltering="getAppliedFilters">
            <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="props" :key="i">
              <component :is="f.type" :properties="f.props" :ref="el => collectFilterRef(`${f.type}_pr_ref`, el, i)"/>
            </template>
          </Filters>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfList
          v-if="prs"
          name="pr_transactions_conf_list"
          ref="transactions_conf_list"
          :aggridColumns="updatedGridColumns"
          :rows="prs"
          :activeTab="activeTab"
          :baseQuery="baseQuery"
          :currentPage="currentPage"
        />
        <el-row v-if="!meta.count && !isFilterLoading" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  // import SelectFilter from '@/components/FilterComponents/SelectFilter'
  // import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import { filterRefsMixin } from '@/Mixins/FilterRefs'
  import TransactionsConfList from '@/components/SigmaClientNew/TransactionsConfList.vue'
  import { retainFiltersOnEntry } from '@/Mixins/SigmaClientNew/transactionGridFilters'

  export default {
    components: {
      Filters,
      KeywordFilter,
      // SelectFilter,
      // AutocompleteFilter,
      ListViewHeader,
      TransactionsConfList
    },
    mixins: [ listViewMixins, filterRefsMixin ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        statusChangeModalVisible: false,
        transactionToUpdate: null,
        activeTab: 'open',
        changingTab: false,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'dateColumn', 'descriptionColumn', 'currencyColumn', 'debitsDecimalAmountColumn', 'batchColumn', 'actionsColumn'],
        statusOptions: [{label: 'DRAFT', value: 'DRAFT'}, {label: 'OPEN', value: 'OPEN'}, {label: 'AUDITED', value: 'AUDITED'}]
      }
    },
    methods: {
      getAppliedFilters () {
        let filters = this.$refs.filters.getFilters
        if (!filters.source_code) {
          const prSourcecode = this.$store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
          filters.source_code = prSourcecode.id
        }
        this.$store.dispatch('clearSetPrFilterInfo')
        let extendedFilters = {}
        if (filters.search) extendedFilters = { search: filters.search }
        sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...filters}))
        sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ page: this.currentPage, extendedFilters: extendedFilters }))
        this.$store.dispatch('setPrFilterInfo', { page: this.currentPage, ...filters })
        this.$refs['transactions_conf_list'].extendedFilters = extendedFilters
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      /**
       * Handles tab changes. The tab decides the statuses the view asks for, so the list is
       * reloaded from scratch with the column filters cleared.
       */
      async changeTab (tab) {
        this.changingTab = true
        this.activeTab = tab
        sessionStorage.removeItem(`gridFilterModel:${this.$route.name}`)
        try {
          this.$store.dispatch('setActiveTab', tab)
          // The tab brings its own criteria, so the column filters start over
          if (this.$refs['transactions_conf_list']) this.$refs['transactions_conf_list'].resetColumnFilters()
          await Promise.all([
            this.$store.dispatch('resetJeMeta'),
            this.$store.dispatch('clearJeState', true),
            this.$store.dispatch('clearFilters'),
            this.$store.dispatch('clearSetPrFilterInfo'),
            this.$store.dispatch('updateTransactionSelection', [])
          ])
          // Clear Search Filter
          this.$refs.keywordFilter_pr_ref.keyword = ''
          await this.loadRows(1)
        } catch (e) {
          console.log(e)
        } finally {
          this.changingTab = false
        }
      },
      /**
       * Handles paginating between PR pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          this.changingPage = true
          const searchText = this.$refs.filters.getFilters.search ? this.$refs.filters.getFilters.search : ''
          await this.loadRows(page, { search: searchText })
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: page, ...this.$refs.filters.getFilters}))
          sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ page: page, extendedFilters: { search: this.$refs.filters.getFilters.search } }))
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
        } finally {
          this.changingPage = false
        }
      },
      async filterRetained () {
        const saved = JSON.parse(sessionStorage.getItem(`gridFilterModel:${this.$route.name}`))
        if (saved && saved.extendedFilters && saved.extendedFilters.search && this.$refs) {
          this.$nextTick(() => {
            this.$refs.keywordFilter_pr_ref.keyword = saved.extendedFilters.search
          })
        }
      }
    },
    computed: {
      ...mapGetters({
        prs: 'getJes',
        meta: 'getJeMetadata',
        currentPage: 'getCurrentJePage',
        getBatchById: 'getBatchById',
        selection: 'getSelectedTransactions',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        getActiveTab: 'getActiveTab',
        getcurrentFilters: 'getFilters',
        getPrFilterInfo: 'getPrFilterInfo',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      /**
       * The statuses the selected tab asks for. Read from the tab rather than kept alongside it,
       * so they are right however the view was entered.
       * @return {Array}
       */
      selectedStatuses () {
        if (this.activeTab === 'posted') return ['POSTED', 'CLOSED']
        if (this.activeTab === 'all') return ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'VOIDED', 'CLOSED']
        return ['DRAFT', 'OPEN', 'AUDITED']
      },
      /**
       * Id of the PR source code, which every payroll query is scoped to
       */
      prSourcecodeId () {
        const prSourcecode = this.$store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
        return prSourcecode ? prSourcecode.id : this.prSourceCode
      },
      /**
       * Criteria this view owns, which the grid's column filters are applied on top of
       */
      baseQuery () {
        const query = { status__in: this.selectedStatuses }
        if (this.prSourcecodeId) query.source_code = this.prSourcecodeId
        return query
      },
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_payrollentry', 'entry.add_payrollentry'])
      },
      hasPermission () {
        return {
          read: this.hasReadPermission
        }
      },
      hasReadPermission () {
        let readPermission = ['entry.view_payrollentry']
        return this.checkIfUserHasRequiredPermissions(readPermission)
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          }
          // {
          //   type: 'selectFilter',
          //   props: {
          //     filterName: 'status',
          //     clearable: true,
          //     opts: this.statusOptions || [],
          //     defaults: this.selectedStatuses || [],
          //     multi: true,
          //     placeholder: 'Status'
          //   }
          // },
          // {
          //   type: 'autocompleteFilter',
          //   props: {
          //     filterName: 'batch',
          //     remote: true,
          //     clearable: true,
          //     action: 'fetchBatchesWithQuery',
          //     clearAction: 'clearBatchState',
          //     getter: 'getBatchesBySearchCriteria',
          //     triggerOnFocus: false,
          //     placeholder: 'Batch',
          //     queryParamType: 'search',
          //     suggestionLabelAttr: 'name'
          //   }
          // },
          // {
          //   type: 'selectFilter',
          //   props: {
          //     filterName: 'batch__bank__currency',
          //     clearable: true,
          //     opts: this.$store.getters.getAllCurrencies.map(c => ({ label: c.code, value: c.id })),
          //     multi: true,
          //     placeholder: 'Currency',
          //     styles: '0 4px'
          //   }
          // }
        ]
      },
      updatedGridColumns () {
        return (this.activeTab === 'posted') ? this.aggridColumns.filter((c) => c !== 'rowSelect') : this.aggridColumns
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('prs').offsetHeight
          const headerHeight = document.getElementById('list-view-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    created () {
      this.activeTab = this.getActiveTab
    },
    beforeUnmount () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    },
    async beforeRouteEnter (to, from, next) {
      // Settled before the grid is built, so it only finds filters to put back on a return trip
      retainFiltersOnEntry(to.name, from.name)
      try {
        const promises = [
          store.dispatch('resetJeMeta'),
          store.dispatch('clearJeState', true),
          store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ]
        // The payroll source code scopes every query this view makes, so it has to be in hand
        // before the grid loads the list
        if (!store.getters.getAllSourcecodes.length) {
          promises.push(store.dispatch('fetchSourcecodesWithQuery'))
        }
        await Promise.all(promises)
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    }
  }
</script>

<style scoped>
  .focussed-btn {
    background-color: #04709d;
    cursor: default;
  }
  .unfocussed-btn {
    background-color: #fff;
    cursor: pointer;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-button--info.is-plain {
    background: #e1f5fe;
    border: 1px solid #bfcbd9;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>
